<template>
    <div v-if="misses != null">
        <span
            v-if="lane !== undefined"
            class="me-2"
            >{{ $tm('shooting.laneshort') }}:{{ lane.toString().padStart(2, '0') }}
        </span>
        <IconComp
            v-for="(miss, shootingNumber) in misses"
            :key="`${shootingNumber}-${miss}`"
            :family="miss ? 'fa-solid' : undefined"
            icon="fa-circle"
            class="small"
        />
        <span class="ms-2 h-100 d-inline-block">
            <span
                class="h-100 d-inline-block"
                :class="penaltyLapsClass"
            >
                {{ missesCount }}/{{ penaltyLapCount ?? 0 }}
            </span>
            <span>/</span>
            <span
                class="h-100 d-inline-block pe-1"
                :class="backupMissesClass"
            >
                {{ backupMissesCount === null || backupMissesCount === undefined ? '-' : backupMissesCount }}
            </span>
        </span>
        <span v-if="!hideBiathlonPicture && biathlonPictureRelativeUrl">
            <img
                width="75px"
                height="20px"
                :src="'https://photo.eqtiming.com/biathlonpictures/' + biathlonPictureRelativeUrl"
            />
        </span>
    </div>
</template>
<script setup lang="ts">
import { IconComp } from '@/components';
import { computed } from 'vue';

// The three numbers are misses / penalty laps / backup misses

const props = defineProps<{
    /**
     * Missed shot array.
     * True are misses, false are hits.
     * Null means we haven't shot yet.
     */
    misses: Array<boolean> | null;
    /**
     * The shooting lane.
     * This is optional because its not always available.
     * If its undefined, then no lane is shown.
     */
    lane?: number;

    /**
     * The number of penalty laps the racecontestant has completed for this shooting.
     * Also optional because it isnt always available.
     * If undefined, then no penalty lap count is shown.
     */
    penaltyLapCount?: number;

    /**
     * The number of misses reported by the shooting backup.
     * This is usually someone with a phone looking at the targets and reporting the misses.
     * There is not always someone doing this, so this is optional.
     */
    backupMissesCount?: number | null;

    /**
     * Wether or not we should show the backup misses warning.
     */
    hasBackupMissesWarning: boolean;

    /**
     * Wether or not we should show the penalty lap warning.
     */
    hasPenaltyLapWarning: boolean;

    /**
     * The relative path of a picture showing the shooting targer
     * This should include the deviceid and the picturename
     * Complete url is `https://photo.eqtiming.com/biathlonpictures/DEVICEID/PICTURENAMEWITHEXTENSION`
     * This expects the `DEVICEID/PICTURENAMEWITHEXTENSION` part
     */
    biathlonPictureRelativeUrl?: string | null;

    /**
     * Prop to hide all the biathlon pictures
     *
     * This exists because rendering all the pictures can be slow, so having an option of turning it off can be good
     */
    hideBiathlonPicture?: boolean;

    /**
     * Name of the user who acknowledged the warnings
     */
    warningsAcknowledgedByName?: string | null;
}>();

const missesCount = computed(() => props.misses?.filter((m) => m === true).length ?? 0);

const penaltyLapsClass = computed(() => {
    if (!props.hasPenaltyLapWarning) {
        return '';
    }

    if (props.warningsAcknowledgedByName) {
        return 'bg-green-100';
    }

    return 'bg-danger-subtle text-white';
});

const backupMissesClass = computed(() => {
    if (!props.hasBackupMissesWarning) {
        return '';
    }

    if (props.warningsAcknowledgedByName) {
        return 'bg-success-subtle';
    }

    return 'bg-danger-subtle text-white';
});
</script>
